import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Redirect } from 'react-router';
import SecuritySettingsForm from '../../components/application/authentication/SecuritySettingsForm';
import TwoFactorSetupForm from '../../components/application/authentication/TwoFactorSetupForm';
import RecoveryCodesModal from '../../components/application/authentication/RecoveryCodesModal';
import Flash from '../../components/helpers/Flash';
import { UserContext } from '../../Application';
import { hasPermissions, PERMISSIONS } from '../../constants/authentication/permissions';
import { flashMessageSelector, flashOpenSelector, flashTypeSelector } from '../../selectors/application';
import { closeFlash } from '../../actions/global';
import {
    twoFactorCodeSelector,
    twoFactorSubmittingSelector,
    twoFactorErrorSelector,
    twoFactorRecoveryCodesSelector,
    twoFactorShowRecoveryCodesSelector,
    twoFactorRemainingRecoveryCodesSelector,
    twoFactorIsResettingSelector,
    twoFactorQRCodeSelector,
    twoFactorSecretSelector
} from '../../selectors/authentication';
import {
    inputTwoFactor,
    hideRecoveryCodes,
    fetchTwoFactorStatus,
    initiate2FAReset,
    verify2FAReset,
    cancel2FAReset
} from '../../actions/twoFactor';

class TwoFactorRecovery extends Component {
    componentDidMount() {
        this.props.fetchTwoFactorStatus();
    }

    componentWillUnmount() {
        // If the user leaves mid-reset (without completing setup), don't keep forcing the setup UI
        // when they come back to this page.
        this.props.cancel2FAReset();
        this.props.inputTwoFactor('', 'code');
    }

    _onCodeInput = (value, key) => {
        const numericValue = value.replace(/\D/g, '');
        this.props.inputTwoFactor(numericValue, key);
    };

    _onReset2FASubmit = () => {
        this.props.initiate2FAReset();
    };

    _onVerifyResetSubmit = () => {
        this.props.verify2FAReset(this.props.code);
    };

    _onCancelReset = () => {
        this.props.cancel2FAReset();
        this.props.inputTwoFactor('', 'code');
    };

    _onRecoveryCodesClose = () => {
        this.props.hideRecoveryCodes();
        // Clear the code input after closing
        this.props.inputTwoFactor('', 'code');
        // Refresh status to get new remaining count
        this.props.fetchTwoFactorStatus();
    };

    _renderContent() {
        // Show recovery codes modal if regeneration was successful
        if (this.props.showRecoveryCodes && this.props.recoveryCodes) {
            return (
                <React.Fragment>
                    <Flash
                        type={this.props.flashType}
                        message={this.props.flashMessage}
                        open={this.props.flashOpen}
                        onClose={this.props.closeFlash}
                    />
                    <RecoveryCodesModal recoveryCodes={this.props.recoveryCodes} onClose={this._onRecoveryCodesClose} />
                </React.Fragment>
            );
        }

        // Show 2FA setup form when resetting 2FA
        if (this.props.isResettingTwoFactor && this.props.qrCode) {
            return (
                <React.Fragment>
                    <Flash
                        type={this.props.flashType}
                        message={this.props.flashMessage}
                        open={this.props.flashOpen}
                        onClose={this.props.closeFlash}
                    />
                    <TwoFactorSetupForm
                        qrCode={this.props.qrCode}
                        secret={this.props.secret}
                        code={this.props.code}
                        onInput={this._onCodeInput}
                        onSubmit={this._onVerifyResetSubmit}
                        onCancel={this._onCancelReset}
                        cancelLabel="BACK TO LOGIN"
                        loading={this.props.loading}
                        errorText={this.props.error}
                    />
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                <Flash
                    type={this.props.flashType}
                    message={this.props.flashMessage}
                    open={this.props.flashOpen}
                    onClose={this.props.closeFlash}
                />
                <SecuritySettingsForm
                    onReset2FASubmit={this._onReset2FASubmit}
                    loading={this.props.loading}
                    remainingRecoveryCodes={this.props.remainingRecoveryCodes}
                />
            </React.Fragment>
        );
    }

    render() {
        return (
            <UserContext.Consumer>
                {user => {
                    // Wait for user data to be fetched before checking permissions
                    if (!user || !user.fetched) {
                        return null;
                    }

                    // Check if user has ACCESS_ADMIN permission (includes god role)
                    if (!hasPermissions(user, PERMISSIONS.ACCESS_ADMIN)) {
                        // Redirect to a not found route which will show 404 message
                        return <Redirect to="/404" />;
                    }

                    return this._renderContent();
                }}
            </UserContext.Consumer>
        );
    }
}

const mapStateToProps = state => ({
    code: twoFactorCodeSelector(state),
    loading: twoFactorSubmittingSelector(state),
    error: twoFactorErrorSelector(state),
    recoveryCodes: twoFactorRecoveryCodesSelector(state),
    showRecoveryCodes: twoFactorShowRecoveryCodesSelector(state),
    remainingRecoveryCodes: twoFactorRemainingRecoveryCodesSelector(state),
    isResettingTwoFactor: twoFactorIsResettingSelector(state),
    qrCode: twoFactorQRCodeSelector(state),
    secret: twoFactorSecretSelector(state),
    flashMessage: flashMessageSelector(state),
    flashOpen: flashOpenSelector(state),
    flashType: flashTypeSelector(state)
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            inputTwoFactor,
            hideRecoveryCodes,
            fetchTwoFactorStatus,
            initiate2FAReset,
            verify2FAReset,
            cancel2FAReset,
            closeFlash
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(TwoFactorRecovery);
